import * as React from 'react';

export default class DateTimeFormatter {
    static _formatDate(datetime, format) {
        const dateFormat = format.full;
        const year = datetime.getFullYear();
        const month = this._formatTimeNumber(datetime.getMonth() + 1);
        const day = this._formatTimeNumber(datetime.getDate());

        return dateFormat.replace('Y', year).replace('m', month).replace('d', day);
    }

    static _formatShortDate(datetime, format) {
        const dateFormat = format.short;
        const month = this._formatTimeNumber(datetime.getMonth() + 1);
        const day = this._formatTimeNumber(datetime.getDate());

        return dateFormat.replace('m', month).replace('d', day);
    }

    static _parseDate(datetime) {
        if (datetime instanceof Date) {
            return datetime;
        } else if (datetime instanceof String || typeof datetime === 'string') {
            if (datetime.match(/(\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2})/)) {
                let dateArray = datetime.split(/[- :]/);
                return new Date(dateArray[0], dateArray[1]-1, dateArray[2], dateArray[3], dateArray[4], dateArray[5]);
            } else {
                return new Date(datetime.split(' ')[0].split('T')[0]);
            }
        } else {
            return datetime.timestamp ? new Date(datetime.timestamp * 1000) : new Date(datetime.sec * 1000);
        }
    }

    static getDate(datetime, format) {
        if (!datetime) {
            return datetime;
        }
        const parsedDate = this._parseDate(datetime);

        return this._formatDate(parsedDate, format);
    }

    static dayOffset(fromDatetime, toDatetime) {
        if (!fromDatetime || !toDatetime) {
            return 0;
        }
        const from = this._parseDate(fromDatetime);
        const to = this._parseDate(toDatetime);
        if (!(from instanceof Date) || !(to instanceof Date) || isNaN(from) || isNaN(to)) {
            return 0;
        }
        const d1 = new Date(from.getFullYear(), from.getMonth(), from.getDate());
        const d2 = new Date(to.getFullYear(), to.getMonth(), to.getDate());

        return Math.round((d2 - d1) / 86400000);
    }

    static getShortDate(datetime, format) {
        if (!datetime) {
            return datetime;
        }

        const parsedDate = this._parseDate(datetime);

        return this._formatShortDate(parsedDate, format);
    }

    static getWeekday(day, fullDate = false) {
        const weekdays = [
            'Su',
            'Mo',
            'Tu',
            'We',
            'Th',
            'Fr',
            'Sa'
        ];
        const fullWeekdays = [
            'Sunday',
            'Monday',
            'Tuesday',
            'Wednesday',
            'Thursday',
            'Friday',
            'Saturday'
        ];

        if (!fullDate) {
            return weekdays[day];
        }
        return fullWeekdays[day];
    }

    static getTime(datetime) {
        datetime = new Date(this._makeDatetimeIosFriendly(datetime));
        return `${this._formatTimeNumber(datetime.getHours())}:${this._formatTimeNumber(datetime.getMinutes())}`;
    }

    static getDurationParts(time) {
        const hours = parseInt(time.sec / 3600, 10);
        const minutes = parseInt((time.sec - hours * 3600) / 60, 10);
        const seconds = (time.sec - minutes * 60);

        return [hours, minutes, seconds];
    }

    static getTimeDiff(next, prev) {
        const diff = next - prev;

        let data = {
            'hours': parseInt(diff / (3600 * 1000), 10),
            'minutes': parseInt(diff / (60 * 1000), 10),
            'seconds': parseInt(diff / 1000, 10)
        };

        data.minutes = data.minutes - data.hours * 60;
        data.seconds = data.seconds - data.minutes * 60;

        return data;
    }

    /**
     * Converts seconds to hours, minutes and seconds
     */
    static secsToTimeParts(secs) {
        const hours = Math.floor(secs / 3600);
        const minutes = Math.floor(secs / 60) - Math.floor(secs / 3600) * 60;
        const seconds = secs - Math.floor(secs / 60) * 60;

        return [hours, minutes, seconds];
    }

    static _formatTimeNumber(value) {
        return value >= 10 ? value : `0${value}`
    }

    /**
     * Takes the following format: '2019-12-31 23:59'. Parses it and created iOS-supported date
     */
    static _makeDatetimeIosFriendly(date) {
        let dateArray = date.split(/[- :]/);
        date = new Date(dateArray[0], dateArray[1]-1, dateArray[2], dateArray[3], dateArray[4], dateArray[5]);
        return date;
    }

    static getMonthOf(month, fullMonth = false) {
        const monthList = [
            'January (of)',
            'February (of)',
            'March (of)',
            'April (of)',
            'May (of)',
            'June (of)',
            'July (of)',
            'August (of)',
            'September (of)',
            'October (of)',
            'November (of)',
            'December (of)'
        ];
        const shortMonthList = [
            'Jan',
            'Feb',
            'Mar',
            'Apr',
            'May',
            'Jun',
            'Jul',
            'Aug',
            'Sep',
            'Oct',
            'Nov',
            'Dec'
        ];

        if (!fullMonth) {
            return shortMonthList[month];
        }
        return monthList[month];
    }

    static timeFormat(hours, minutes, hoursTranslation, minutesTranslation, short = true) {
        if (short) {
            return <span>{hours ? `${hours} ${hoursTranslation}` : ``} {minutes} {minutesTranslation}</span>
        }

        return <span>{hours}{hoursTranslation} {minutes}{minutesTranslation}</span>
    }

    static directionFormat(cityName, airportShort, airportName) {
        return (
            <span>
                <span><strong>{cityName}({airportShort}), </strong></span>
                {airportName}
            </span>
        )
    }

    static getCurrentDate () {
        return new Date().toJSON().slice(0,10).replace(/-/g,'-');
    }

    // Helper for parsing dates with no times.
    // Use it instead of new Date('xxxx-xx-xx'). Reason: iOS date bug.
    static createDate(date) {
        const parts = date.split(/[- :]/);
        const dateObject = new Date(parts[0], parts[1]-1, parts[2]);

        return new Date(dateObject);
    }
}



// WEBPACK FOOTER //
// ./src/helpers/DateTimeFormatter.js